import { useState } from "react";
import { ENV } from "Config";
import { BbotModal, Input, notification, Radio, Space } from "top-component-library";
import { generalErrorAlert } from "util/Utils";
import { ENVS_REQUIRING_HOSTNAME_PREFIX, isValidHostname } from "./utils";

const DEFAULT_ACTIVE_CHOICE = "addCustomerToExistingHostnameConfiguration";

// Mirrors add-new-hostname-dialog.html/addNewHostnameController: one modal, three
// mutually exclusive steps driven by the backend's response. Step 1 tries to
// register a brand new hostname; if it's already active or archived elsewhere,
// the backend asks how to resolve that instead of creating it outright.
const AddHostnameDialog = ({ visible, onClose, customerName, onAddHostname, onAddHostnameFromPrompt }) => {
  const [step, setStep] = useState("adding_hostname");
  const [hostname, setHostname] = useState("");
  const [connectedCustomers, setConnectedCustomers] = useState([]);
  const [activeChoice, setActiveChoice] = useState(DEFAULT_ACTIVE_CHOICE);
  const [errorCode, setErrorCode] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    setStep("adding_hostname");
    setHostname("");
    setConnectedCustomers([]);
    setActiveChoice(DEFAULT_ACTIVE_CHOICE);
    setErrorCode(null);
    onClose();
  };

  const submitNewHostname = async () => {
    const trimmedHostname = hostname.trim();
    if (!isValidHostname(trimmedHostname, ENV)) {
      notification.error({
        message: "Invalid format for hostname. Please verify that you are following the rules listed above.",
      });
      return;
    }
    setIsSaving(true);
    try {
      const { prompt, connectedCustomers: connected } = await onAddHostname(trimmedHostname);
      if (prompt) {
        setStep(prompt);
        setConnectedCustomers(connected);
      } else {
        notification.success({ message: "Domain added successfully!" });
        close();
      }
    } catch (error) {
      if (error.response?.data?.errorCode) {
        setErrorCode(error.response.data.errorCode);
        generalErrorAlert(error, "Trouble assigning domains to this customer. Check the domain name field for errors.");
      } else {
        generalErrorAlert(error, "Trouble adding hostname. Please refresh and try again");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const submitFromPrompt = async () => {
    const trimmedHostname = hostname.trim();
    const payload =
      step === "hostname_is_active_prompt"
        ? {
            addHostnameChoice: activeChoice,
            ...(activeChoice === DEFAULT_ACTIVE_CHOICE
              ? { hostnameToAdd: trimmedHostname }
              : { hostnameToTransfer: trimmedHostname }),
          }
        : {
            addHostnameChoice: "completeTransferHostnameConfigurationToThisCustomer",
            hostnameToTransfer: trimmedHostname,
          };

    setIsSaving(true);
    try {
      await onAddHostnameFromPrompt(payload);
      notification.success({ message: "Domain added successfully!" });
      close();
    } catch (error) {
      generalErrorAlert(error, "Trouble adding hostname. Please refresh and try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const titles = {
    adding_hostname: "Add Domain Name",
    hostname_is_active_prompt: "Hostname is Currently Active",
    hostname_is_inactive_prompt: "Hostname is Currently Archived",
  };

  return (
    <BbotModal
      title={
        <span>
          {titles[step]} <small>(Admin only)</small>
        </span>
      }
      visible={visible}
      onCancel={close}
      okText="Save"
      okButtonProps={{ loading: isSaving, disabled: step === "adding_hostname" && !hostname.trim() }}
      onOk={step === "adding_hostname" ? submitNewHostname : submitFromPrompt}
    >
      {step === "adding_hostname" && (
        <>
          <p>
            Important: for this to work, the domain{" "}
            <a href="https://central.bbot.menu/article/381-dns-nameservers">must point to our server.</a> To verify,
            visit the domain and confirm you see the TOP by DoorDash page.
          </p>
          <p>
            The hostname should be entered like <em>mysite.menu</em> with no <em>www.</em> or <em>http://</em> or
            trailing slashes.
          </p>
          <p>
            Hostnames cannot contain any whitespace or commas. Their top level domain should be 2 - 10 characters long,
            and they cannot begin or end with a hyphen.
          </p>
          {ENVS_REQUIRING_HOSTNAME_PREFIX.includes(ENV) && (
            <p>
              Your hostname must also begin with <em>{ENV}.</em>
            </p>
          )}
          {errorCode && <p style={{ color: "red" }}>{errorCode}</p>}
          <label htmlFor="add-hostname-input">Domain Name</label>
          <Input
            id="add-hostname-input"
            placeholder="bbot.menu"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
          />
        </>
      )}

      {step === "hostname_is_active_prompt" && (
        <>
          <p>
            You are attempting to create {hostname}, which is currently being used by {connectedCustomers.join(", ")}.
            What would you like to do?
          </p>
          <Radio.Group value={activeChoice} onChange={(e) => setActiveChoice(e.target.value)}>
            <Space direction="vertical">
              <Radio value="addCustomerToExistingHostnameConfiguration">
                Add {hostname} to {customerName} and all of {customerName}&apos;s locations.
              </Radio>
              <Radio value="partialTransferHostnameConfigurationToThisCustomer">
                Add {hostname} to {customerName} and all of {customerName}&apos;s locations AND make {customerName} the
                managing customer for {hostname}.
              </Radio>
              <Radio
                value="completeTransferHostnameConfigurationToThisCustomer"
                style={{ color: "red", fontWeight: "bold" }}
              >
                Remove {hostname} from ALL OTHER customers and locations and transfer {hostname} to {customerName}{" "}
                entirely. IMPORTANT: This will remove all previously connected customers, locations, assets, and BOS
                templates from this hostname.
              </Radio>
            </Space>
          </Radio.Group>
        </>
      )}

      {step === "hostname_is_inactive_prompt" && (
        <>
          <p>
            You are attempting to create {hostname}, which is currently archived. It was previously used by{" "}
            {connectedCustomers.join(", ")}. If you wish to remove {hostname} from all these customers and their
            locations, delete any BOS Builder templates previously created, and have {customerName} assume complete
            ownership of {hostname}, press the &quot;Save&quot; button below.
          </p>
          <p>
            After un-archiving this site, the live site may not be available for up to 24 hours due to delays in
            certification of live sites. If the site is still not live after 24 hours, please contact TOP by DoorDash
            Support.
          </p>
        </>
      )}
    </BbotModal>
  );
};

export default AddHostnameDialog;
